/* CM sticky Add to Cart on theme product pages (every product template except 2026-cm-pdp).
   <cm-theme-product> stands in for the CM buy box: the sticky bar (<hs-sticky-atc>, hs-pdp.js)
   talks to it exactly as it talks to the buy box, and it drives the theme's own product form.
   Adding to cart always goes through the theme's own Add to Cart button, so everything that
   form adds (custom sizes, edging, ship notes, setup fees) is kept.

   Carried over from the Zoorix custom script:
   1. Options sync to the theme's dropdowns; after a Colour change the Size is re-applied
      (the theme rebuilds the size list when the colour changes).
   2. Picking a "Custom" size opens the theme's custom-size form and scrolls to it; a standard
      size hides it again.
   3. Add to Cart does not add for logo products (title contains "logo") or a custom size
      (selected, or the only size): it scrolls to the product options instead, opening the
      custom-size form for a custom size. */
(() => {
  if (customElements.get('cm-theme-product')) return;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const money = (cents, format) => {
    const n = (cents || 0) / 100;
    const fmt = (d, sep, dec) => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (f ? dec + f : '');
    };
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(2, ',', '.'), amount_no_decimals: fmt(0, ',', '.'),
      amount_with_comma_separator: fmt(2, '.', ','), amount_no_decimals_with_comma_separator: fmt(0, '.', ',')
    })[k] || fmt(2, ',', '.'));
  };

  // Set a theme dropdown the way a person would, so every theme script listening to it reacts.
  const humanSet = (el, value) => {
    if (!el) return;
    el.value = value;
    ['mousedown', 'mouseup'].forEach(t => el.dispatchEvent(new MouseEvent(t, { bubbles: true, cancelable: true, view: window })));
    ['change', 'input', 'blur'].forEach(t => el.dispatchEvent(new Event(t, { bubbles: true })));
  };
  const isCustom = v => String(v || '').toLowerCase().includes('custom');

  class CmThemeProduct extends HTMLElement {
    connectedCallback() {
      try { this.d = JSON.parse($('script[type="application/json"]', this).textContent); } catch (_) { return; }
      this.form = $('form.product-single__form[action*="/cart/add"]') || $('form[action*="/cart/add"][id^="AddToCartForm"]');
      if (!this.form) return;
      this.variantId = this.d.current;
      // Start from the theme's own quantity (some products open at their minimum, not 1).
      this.qty = parseInt(this.qtyInput.value, 10) || 1;
      this.syncing = false;

      // Follow the theme: its variant event, and any change to its own dropdowns or quantity.
      document.addEventListener('variant:change', e => {
        const v = e.detail && e.detail.variant;
        if (v && v.id) { this.variantId = v.id; this.render(); }
      });
      document.addEventListener('change', e => {
        if (this.syncing) return;
        if (e.target.matches && (e.target.matches('select[data-variant-input]') || e.target.matches('.js-qty__num'))) {
          setTimeout(() => this.fromTheme(), 60);
        }
      });
      document.addEventListener('hs:submit', () => this.submit());
      customElements.whenDefined('hs-sticky-atc').then(() => this.render());
      // Minimum-quantity apps (MinMaxify) set the theme's quantity after load and reset it to the
      // minimum, without a change event; follow it so the bar shows what will be added.
      setInterval(() => {
        const q = parseInt(this.qtyInput.value, 10);
        if (q > 0 && q !== this.qty) { this.qty = q; this.render(); }
      }, 700);
      this.render();
    }

    // The bar's fallback (when it can't add) and the custom/logo guard scroll here.
    get optionsTarget() {
      return $('.variant-wrapper', this.formScope) || $('.product-single__variants', this.formScope) || $('.product-form') || this.form;
    }
    get formScope() { return this.form.closest('[data-product-blocks], .product-single__meta, .product-section, .shopify-section') || document; }
    get qtyInput() { return $('.js-qty__num', this.formScope) || { value: this.qty }; }
    themeSelect(i) { return $(`select[data-variant-input][data-index="option${i + 1}"]`, this.formScope); }
    optionIndex(re) { return this.d.options.findIndex(n => re.test(String(n).toLowerCase())); }
    get sizeIndex() { return this.optionIndex(/size|length|dimension/); }

    variant() { return this.d.variants.find(v => v.id === +this.variantId) || null; }

    fromTheme() {
      const vals = this.d.options.map((_, i) => this.themeSelect(i)?.value);
      const v = this.d.variants.find(x => x.options.every((o, i) => vals[i] == null || o === vals[i]));
      if (v) this.variantId = v.id;
      const q = parseInt(this.qtyInput.value, 10);
      if (q > 0) this.qty = q;
      this.render();
    }

    toggleCustomForm(show) {
      const f = document.getElementById('custom-size-form-field-container');
      if (f) f.style.display = show ? 'block' : 'none';
    }
    scrollToOptions() {
      const t = this.optionsTarget;
      if (t) t.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }

    // Called by the sticky bar (desktop dropdowns and the phone panel).
    setOption(i, value) {
      const sel = this.themeSelect(i);
      if (!sel) return;
      this.syncing = true;
      humanSet(sel, value);
      const name = String(this.d.options[i] || '').toLowerCase();
      const si = this.sizeIndex;
      if (/colo/.test(name) && si > -1) {
        // The theme rebuilds the size list for the new colour: put the chosen size back.
        const wanted = this.variant()?.options[si];
        setTimeout(() => {
          const s = this.themeSelect(si);
          if (s && wanted && Array.from(s.options).some(o => o.value === wanted)) humanSet(s, wanted);
          this.fromTheme();
        }, 500);
      }
      if (i === si) {
        if (isCustom(value)) { this.toggleCustomForm(true); this.scrollToOptions(); }
        else this.toggleCustomForm(false);
      }
      setTimeout(() => { this.syncing = false; this.fromTheme(); }, 120);
    }

    setQty(n) {
      this.qty = Math.max(1, Math.min(99, parseInt(n, 10) || 1));
      const q = this.qtyInput;
      if (q.tagName) { q.value = this.qty; q.dispatchEvent(new Event('change', { bubbles: true })); }
      this.render();
    }
    setGfci() {}

    get isLogo() {
      const t = $('.product-single__title') || $('.h2.product-single__title');
      return !!(t && t.textContent.toLowerCase().includes('logo'));
    }
    get isCustomChoice() {
      const si = this.sizeIndex;
      if (si < 0) return false;
      const sel = this.themeSelect(si);
      const values = sel ? Array.from(sel.options).map(o => o.value) : this.d.values[si] || [];
      const current = sel ? sel.value : this.variant()?.options[si];
      return (values.length === 1 && isCustom(values[0])) || isCustom(current);
    }

    submit() {
      if (this.isLogo || this.isCustomChoice) {
        if (this.isCustomChoice) this.toggleCustomForm(true);
        this.scrollToOptions();
        return;
      }
      // Make sure the form carries the variant chosen in the bar, even if the theme's own
      // variant script hasn't caught up (or failed to load).
      this.fromTheme();
      const idField = this.form.querySelector('[name="id"]') || document.querySelector(`[name="id"][form="${this.form.id}"]`);
      if (idField && this.variantId && String(idField.value) !== String(this.variantId)) {
        if (idField.tagName === 'SELECT' && !Array.from(idField.options).some(o => o.value == this.variantId)) {
          idField.add(new Option('', this.variantId));
        }
        idField.value = this.variantId;
      }
      const btn = $('[data-add-to-cart]', this.form) || $('button[name="add"]', this.form) || $('[type="submit"]', this.form);
      if (btn && !btn.disabled) btn.click(); else this.scrollToOptions();
    }

    render() {
      const v = this.variant();
      const f = this.d.moneyFormat;
      const sold = !!(v && !v.available);
      document.dispatchEvent(new CustomEvent('hs:state', { detail: {
        ready: !!(v && v.available),
        label: 'Add to Cart',
        summary: v ? v.options.filter(o => !/^default title$/i.test(o)).join(' · ') : '',
        price: v ? money(v.price, f) : '',
        onSale: !!(v && v.compare > v.price),
        options: v ? v.options : [],
        qty: this.qty,
        max: 99,
        soldOut: sold,
        needsChoice: false,
        hasGfci: false,
        image: v && v.img ? v.img : this.d.img,
        addon: ''
      } }));
    }
  }
  customElements.define('cm-theme-product', CmThemeProduct);
})();
